import React, { useState, useEffect, useContext } from "react";
import "./VenueList.scss";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import areanaImg from "../../../assets/areanaDefault.png";
import UserContext from "../../../context/UserContext"; // Ensure this path is correct
import Loader from "../../../components/Loader/Loader";
import LocationDropdown from "./LocationDropdown";
import SportDrop from "./SportDrop";
import NoVenue from "./NoVenue";

function VenueList() {
  const navigate = useNavigate();
  const { userDetail } = useContext(UserContext); // Access user details from context

  // State for dropdowns and venue data
  const [location, setLocation] = useState("Chennai");
  const [sport, setSport] = useState("All");
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Static options for dropdowns (can be fetched from API later)
  const locationOptions = ["Chennai", "Bangalore", "Mumbai"];
  const sportOptions = [
    "All",
    "Cricket",
    "Padel",
    "Football",
    "Badminton",
    "Tennis",
    "Pickleball",
    "Table Tennis",
  ];

  // Fetch venues based on selected city and sport
  const fetchVenues = async () => {
    setLoading(true);
    setError(null);

    const url =
      "https://ballersapi.ballerslife.co/api/v1/memberships/sports_facility/get_filtered_facility_overview";
    const accessToken = window.localStorage.getItem("access_token");

    if (!accessToken) {
      setError("No access token found. Please log in again.");
      toast.error("No access token found. Please log in again.");
      setLoading(false);
      return;
    }

    const body = {
      city: location.toLowerCase(),
      sport: sport.toLowerCase() === "all" ? [] : [sport.toLowerCase()], // Send an empty array if "All" is selected
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.status === 200) {
        const mappedVenues = data.map((facility) => ({
          id: facility.facility_id,
          name: facility.facility_name,
          location: `${facility.locality}, ${facility.city}`,
          sports: facility.available_sports,
          image: facility.facility_image || areanaImg,
        }));
        setVenues(mappedVenues);
      } else {
        setError("Failed to fetch venues. Please try again.");
        // toast.error(data.detail || "Failed to fetch venues.");
      }
    } catch (err) {
      setError("An error occurred while fetching venues.");
      // toast.error("An error occurred while fetching venues.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch venues when location or sport changes
  useEffect(() => {
    document.title = `Reserve | Ballers`;
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    fetchVenues();
  }, [location, sport]);

  // Handle card click to navigate to details page
  const handleCardClick = (facilityId) => {
    navigate(`/dashboard/reserveDetails/${facilityId}`);
  };

  // Handle keyboard navigation for accessibility
  const handleCardKeyDown = (e, facilityId) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick(facilityId);
    }
  };

  // If userDetail is not available, show a loading state
  if (!userDetail) {
    return <Loader />;
  }

  // Show loader for the entire page if loading is true
  if (loading) {
    return <Loader />;
  }

  return (
    <div className="venue-list">
      {/* Header Section */}
      <div className="header">
        <p className="greeting">
          Hi {userDetail?.member_name || "User"} ,
          {/* Fallback for member_name */}
        </p>
        <div className="filters">
          <div className="dropdown">
            <label htmlFor="location-select">Location:</label>
            <LocationDropdown
              location={location}
              setLocation={setLocation}
              locationOptions={[
                "Chennai",
                "Bangalore",
                "Delhi",
                "Hyderabad",
                "Mumbai",
                "Pune",
              ]}
            />
          </div>
          <div className="dropdown2">
            <label htmlFor="sport-select">Sports:</label>
            <SportDrop
              sport={sport}
              setSport={setSport}
              // sportOptions={["badminton", "pickleball", "tennis", "football"]}
              sportOptions={["pickleball", "badminton"]}
            />
          </div>
        </div>
      </div>

      {/* Venue Cards */}
      <div className="games-grid">
        {error ? (
          <NoVenue />
        ) : venues ? (
          venues.map((venue) => (
            <div
              key={venue.id}
              className="game-card"
              onClick={() => handleCardClick(venue.id)}
              onKeyDown={(e) => handleCardKeyDown(e, venue.id)} // Add keyboard navigation
              role="button" // Accessibility role
              tabIndex={0} // Make it focusable
              style={{ cursor: "pointer" }}
              aria-label={`View details for ${venue.name}`}
            >
              <img src={venue.image} alt={venue.name} className="venue-image" />
              <div className="venue-info">
                <h3 className="venue-name">{venue.name}</h3>
                <p className="venue-location">{venue.location}</p>
                <p className="venue-sports">
                  {venue.sports
                    .map(
                      (sport) => sport.charAt(0).toUpperCase() + sport.slice(1)
                    )
                    .join(", ")}
                </p>
              </div>
            </div>
          ))
        ) : (
          <NoVenue />
        )}
      </div>
    </div>
  );
}

export default VenueList;
